import debounce from 'lodash/debounce';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { UpdateEvent } from './types';
import { useSocket } from './useSocket';

export type SubscriptionOption = { type: string; id?: string; tenantId?: string; skip?: boolean };

export type SubscriptionOptions = SubscriptionOption[];

const DEBOUNCE_WAIT = 500;
const DEBOUNCE_MAX_WAIT = 500;

const isOptionDifferent = (lhs: SubscriptionOption, rhs: SubscriptionOption) => {
  return lhs.type !== rhs.type || lhs.id !== rhs.id || lhs.tenantId !== rhs.tenantId || lhs.skip !== rhs.skip;
};

const someOptionDifferent = (lhs: SubscriptionOptions, rhs: SubscriptionOptions) => {
  return lhs.length !== rhs.length || lhs.some((o, idx) => isOptionDifferent(o, rhs[idx]));
};

export const useSubscription = (opt: SubscriptionOption, callback: (event: UpdateEvent) => void) => {
  const [option, setOption] = useState<SubscriptionOption>(opt);
  const { socket, isConnected } = useSocket();

  useEffect(() => {
    setOption(prev => (isOptionDifferent(prev, opt) ? opt : prev));
  }, [opt]);

  useEffect(() => {
    const subscriptionId = uuidv4();
    let isSubscribed = false;
    const debouncer = debounce(callback, DEBOUNCE_WAIT, { maxWait: DEBOUNCE_MAX_WAIT });
    const listener = (event: UpdateEvent) => {
      if (event.subscriptionId === subscriptionId) {
        debouncer(event);
      }
    };

    if (isConnected && !option.skip) {
      socket.on('update-event', listener);

      socket.emit('subscribe', {
        id: subscriptionId,
        updateType: option.type,
        updateId: option.id,
        updateTenantId: option.tenantId,
      });
      isSubscribed = true;
    }

    return () => {
      if (isSubscribed) {
        socket.off('update-event', listener);
        console.debug('unsubscribe ', subscriptionId);
        socket.emit('unsubscribe', subscriptionId);
      }
    };
  }, [socket, isConnected, option, callback]);
};

export const useSubscriptions = (opts: SubscriptionOptions, callback: (event: UpdateEvent) => void) => {
  const [options, setOptions] = useState<SubscriptionOptions>(opts);
  const { socket, isConnected } = useSocket();

  useEffect(() => {
    setOptions(prev => {
      return someOptionDifferent(prev, opts) ? opts : prev;
    });
  }, [opts]);

  useEffect(() => {
    if (!isConnected || options.length === 0 || options.every(o => o.skip)) {
      return;
    }

    const subscriptions = new Map<string, SubscriptionOption>();
    options
      .filter(o => !o.skip)
      .forEach(o => {
        subscriptions.set(uuidv4(), o);
      });

    const debouncer = debounce(callback, DEBOUNCE_WAIT, { maxWait: DEBOUNCE_MAX_WAIT });
    const listener = (event: UpdateEvent) => {
      if (subscriptions.has(event.subscriptionId)) {
        debouncer(event);
      }
    };
    socket.on('update-event', listener);
    subscriptions.forEach((option, subscriptionId) => {
      socket.emit('subscribe', {
        id: subscriptionId,
        updateType: option.type,
        updateId: option.id,
        updateTenantId: option.tenantId,
      });
    });

    return () => {
      socket.off('update-event', listener);
      subscriptions.forEach((_, subscriptionId) => {
        socket.emit('unsubscribe', subscriptionId);
      });
    };
  }, [socket, isConnected, options, callback]);
};
